import { FC, MouseEvent, useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { getImageUrl, showImageWhenLoaded } from '../common/Media';
import { getReadablePalette, ReadablePalette } from '../common/Color';
import { u } from '../common/Units';
import { MEDIA_PREFIX } from '../Settings';
import { DMItemWithOwner } from '../services/DigitaltMuseum';
import { DMArticle as DMArticleData, DMArticleBlock, DMArticleImage, fetchDMArticle } from '../services/DigitaltMuseumArticle';
import { LightboxItem, LightboxWrapper } from './LightboxModal';
import Loader from './Loader';
import DMResultCard from './DMResultCard';
import CardGrid from './CardGrid';
import DMLinkPopup, { DMLink } from './DMLinkPopup';

interface DMArticleProps {
  /** DM artifact UUID of the article */
  uuid: string,
  palette: ReadablePalette,
  /** Called with the color behind the main image (at the top of the page, behind the header) */
  onHeroColor?: (color?: string) => void,
  /** Open an object from an object list: as a page (articles, folders, ...) or in the object modal */
  onOpenObject: (item: DMItemWithOwner) => void
}

const Wrapper = styled.article<{ $palette: ReadablePalette }>`
  max-width: ${u(1000)};
  margin: 0 auto;
  padding: ${u(10)} ${u(65)} ${u(80)};
  color: ${p => p.$palette.textColor};
  line-height: 1.6;

  /* Only the article's own text, not the object cards */
  & > h1 {
    margin: 0 0 ${u(10)};
    font-size: 2.2em;
    line-height: 1.15;
    /* Inter has slightly tighter letter spacing than Akkurat had (about 0.01em less per letter) */
    letter-spacing: 0.01em;
  }

  & section > h2 {
    margin: ${u(50)} 0 ${u(16)};
    font-size: 1.4em;
    line-height: 1.25;
  }

  & .text :is(h3, h4, h5, h6) {
    margin: ${u(30)} 0 ${u(8)};
    font-size: 1.1em;
    line-height: 1.3;
  }

  & .text :is(p, ul, ol) {
    margin: 0 0 ${u(18)};
  }

  & .text li + li {
    margin-top: ${u(6)};
  }

  /* Links can not be followed in the kiosk. A tap shows a QR code */
  & .text a[data-href] {
    color: inherit;
    text-decoration: underline;
    text-underline-offset: 0.2em;
    cursor: pointer;
  }

  & .text blockquote {
    margin: ${u(24)} 0;
    padding-left: ${u(24)};
    border-left: ${u(4)} solid ${p => p.$palette.borderColor};
    font-style: italic;
  }
`;

/** Space around the main image and its caption */
const HERO_SPACE = 24;

/**
 * Main image at the very top of the page (behind the header), cropped to the width of the article,
 * on a band in the image's own color across the whole screen, like on DigitaltMuseum
 */
const Hero = styled.figure<{ $background: string, $palette: ReadablePalette }>`
  /* Up behind the header (PageViewport's padding-top) */
  margin: calc(${u(139)} * -1) 0 ${u(44)};
  /* The space above the image equals the caption and the space below it, so the image is centered in the band */
  padding: calc(${u(2 * HERO_SPACE)} + 1.16em) ${u(65)} ${u(HERO_SPACE)};
  background-color: ${p => p.$background};

  &.noCaption {
    padding-top: ${u(HERO_SPACE)};
  }

  & .image, & figcaption {
    max-width: ${u(870)};
    margin: 0 auto;
  }

  & .image {
    display: block;
    height: ${u(400)};
    cursor: zoom-in;
  }

  & img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
    opacity: 0;
    transition: opacity 0.3s;
  }

  & figcaption {
    margin-top: ${u(HERO_SPACE)};
    /* One line: 0.8em × 1.45 = 1.16em, used in the top padding */
    font-size: 0.8em;
    line-height: 1.45;
    color: ${p => p.$palette.mutedTextColor};
  }
`;

const Lead = styled.div`
  /* Clear space between the lead and the content. Collapses with the top margin of a following image grid */
  margin-bottom: ${u(80)};
  font-size: 1.2em;
  font-weight: 600;
  line-height: 1.45;

  && > :last-child {
    margin-bottom: 0;
  }
`;

const Byline = styled.p<{ $palette: ReadablePalette }>`
  margin: 0 0 ${u(24)};
  color: ${p => p.$palette.mutedTextColor};
  font-size: 0.9em;
`;

/** Text block. Floats an image left or right when the article says so */
const TextBlock = styled.section`
  /* Contain floated images */
  display: flow-root;

  & > figure.left, & > figure.right {
    width: 42%;
    margin-top: ${u(6)};
    margin-bottom: ${u(18)};
  }

  & > figure.left {
    float: left;
    margin-right: ${u(36)};
  }

  & > figure.right {
    float: right;
    margin-left: ${u(36)};
  }
`;

const Figure = styled.figure<{ $palette: ReadablePalette }>`
  margin: ${u(30)} 0;

  & .image {
    display: block;
    border-radius: ${u(12)};
    overflow: hidden;
    background-color: ${p => p.$palette.imageBackgroundColor};
    cursor: zoom-in;
  }

  & img {
    display: block;
    width: 100%;
    height: auto;
    /* Tall images are shown whole, centered */
    max-height: ${u(760)};
    object-fit: contain;
    opacity: 0;
    transition: opacity 0.3s;
  }

  & figcaption {
    margin-top: ${u(10)};
    font-size: 0.8em;
    line-height: 1.45;
    color: ${p => p.$palette.mutedTextColor};
  }
`;

const ImageGrid = styled.div<{ $columns: number }>`
  display: grid;
  grid-template-columns: repeat(${p => p.$columns}, minmax(0, 1fr));
  gap: ${u(24)};
  align-items: start;
  margin: ${u(30)} 0;

  & > figure {
    margin: 0;
  }
`;

/** Slideshow: a horizontal, touch scrollable row of images */
const ImageRow = styled.div`
  display: flex;
  gap: ${u(20)};
  margin: ${u(30)} calc(${u(65)} * -1);
  padding: 0 ${u(65)};
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  scroll-padding: 0 ${u(65)};

  & > figure {
    flex: 0 0 80%;
    margin: 0;
    scroll-snap-align: start;
  }

  & .image {
    height: ${u(520)};
  }

  & img {
    height: 100%;
    object-fit: contain;
  }
`;

/** Four DM cards across, also in portrait */
const ObjectGrid = styled(CardGrid).attrs({ $columns: 4, $portraitColumns: 4 })`
  margin: ${u(24)} 0 ${u(30)};

  /* Smaller text, so four fit in the article column. The DM view scales with the screen, so the cards keep their own radius */
  & > li {
    font-size: 0.85em;
    border-radius: ${u(12)};
  }
`;

/** Caption and credit as one line */
const getCaption = (image: DMArticleImage): string => [image.caption, image.credit].filter(Boolean).join(" · ");

interface ZoomableImageProps {
  image: DMArticleImage,
  dimension: string,
  lazy?: boolean
}

/**
 * An image that opens in the lightbox when tapped
 * @returns {JSX.Element} Component template
 */
const ZoomableImage: FC<ZoomableImageProps> = ({ image, dimension, lazy }) => (
  <LightboxItem media={{ url: image.url, media_type: "dm", title: image.caption || image.credit, description: image.caption && image.credit }}>
    <div className="image">
      <img src={`${MEDIA_PREFIX}${getImageUrl(image.url, { dimension })}`} alt={image.caption || ""} loading={lazy ? "lazy" : undefined} onLoad={showImageWhenLoaded}/>
    </div>
  </LightboxItem>
);

interface ImageProps {
  image: DMArticleImage,
  palette: ReadablePalette,
  className?: string
}

/**
 * An image in the article, with caption and credit
 * @returns {JSX.Element} Component template
 */
const Image: FC<ImageProps> = ({ image, palette, className }) => (
  <Figure $palette={palette} className={className}>
    <ZoomableImage image={image} dimension="1600x1600" lazy={true}/>
    {getCaption(image) && <figcaption>{getCaption(image)}</figcaption>}
  </Figure>
);

/**
 * The main image across the whole width, on the image's color from DM (or a neutral color)
 * @returns {JSX.Element} Component template
 */
const HeroImage: FC<ImageProps> = ({ image, palette }) => (
  <Hero $background={image.bgColor || palette.imageBackgroundColor} $palette={image.bgColor ? getReadablePalette(image.bgColor) : palette} className={getCaption(image) ? undefined : "noCaption"}>
    <ZoomableImage image={image} dimension="2000x2000"/>
    {getCaption(image) && <figcaption>{getCaption(image)}</figcaption>}
  </Hero>
);

interface BlockProps {
  block: DMArticleBlock,
  palette: ReadablePalette,
  onOpenObject: (item: DMItemWithOwner) => void
}

/**
 * A block in the article
 * @returns {JSX.Element} Component template
 */
const Block: FC<BlockProps> = ({ block, palette, onOpenObject }) => {
  const title = block.title && <h2>{block.title}</h2>;
  const text = block.html && <div className="text" dangerouslySetInnerHTML={{ __html: block.html }}/>;

  switch (block.type) {
    case "text":
      return (
        <TextBlock>
          {title}
          {block.images.map((image, i) => <Image key={i} image={image} palette={palette} className={block.imagePosition}/>)}
          {text}
        </TextBlock>
      );

    case "media": {
      const Container = block.layout === "slideshow" && block.images.length > 1 ? ImageRow : ImageGrid;
      return (
        <section>
          {title}
          {text}
          <Container $columns={block.images.length > 1 ? 2 : 1}>
            {block.images.map((image, i) => <Image key={i} image={image} palette={palette}/>)}
          </Container>
        </section>
      );
    }

    case "objects":
      return (
        <section>
          {title}
          {text}
          <ObjectGrid>
            {block.items.map(item => <DMResultCard key={item.uniqueId} item={item} ownerName={item.ownerName} palette={palette} onClick={() => onOpenObject(item)}/>)}
          </ObjectGrid>
        </section>
      );
  }
}

/**
 * A DigitaltMuseum article (subtype "article"), rebuilt from DM's article data: title, lead, author,
 * main image, text (optionally with images beside it), images as a grid or a scrollable row, and lists
 * of DM objects. Images open in the lightbox. Links show a QR code (DMLinkPopup) instead of being followed.
 * @returns {JSX.Element} Component template
 */
const DMArticle: FC<DMArticleProps> = ({ uuid, palette, onHeroColor, onOpenObject }) => {
  const { t } = useTranslation();
  const [article, setArticle] = useState<DMArticleData | null>(null);
  const [hasError, setHasError] = useState<boolean>(false);
  const [selectedLink, setSelectedLink] = useState<DMLink | null>(null);

  /**
   * Show a QR code when a link in the text is tapped
   */
  const onClick = useCallback((e: MouseEvent) => {
    const link = (e.target as HTMLElement)?.closest?.("a[data-href]");
    const url = link?.getAttribute("data-href");
    if (link && url) {
      e.preventDefault();
      setSelectedLink({ url, label: link.textContent?.trim() || url });
    }
  }, []);

  useEffect(() => {
    let isSubscribed = true;
    setArticle(null);
    setHasError(false);
    fetchDMArticle(uuid).then(result => {
      if (!isSubscribed) { return; }
      setArticle(result);
      onHeroColor?.(result.image ? (result.image.bgColor || palette.imageBackgroundColor) : undefined);
    }).catch(() => {
      isSubscribed && setHasError(true);
    });
    return () => {
      isSubscribed = false;
      onHeroColor?.(undefined);
    };
  }, [uuid]); // eslint-disable-line react-hooks/exhaustive-deps

  if (hasError) { return <Wrapper $palette={palette}><Byline $palette={palette}>{t("dm.objectError")}</Byline></Wrapper> }
  if (!article) { return <Loader variant="page" color={palette.mutedTextColor}/> }

  return (
    <LightboxWrapper>
      <div onClick={onClick}>
        {/* Same order as on DigitaltMuseum: main image, title, byline, lead */}
        {article.image && <HeroImage image={article.image} palette={palette}/>}
        <Wrapper $palette={palette}>
          <h1>{article.title}</h1>
          {(article.author || article.ownerName) && (
            <Byline $palette={palette}>{[article.author && t("dm.article.author", { name: article.author }), article.ownerName].filter(Boolean).join(" · ")}</Byline>
          )}
          {article.leadHtml && <Lead className="text" dangerouslySetInnerHTML={{ __html: article.leadHtml }}/>}
          {article.blocks.map((block, i) => <Block key={i} block={block} palette={palette} onOpenObject={onOpenObject}/>)}
        </Wrapper>
        {selectedLink && <DMLinkPopup link={selectedLink} palette={palette} onClose={() => setSelectedLink(null)}/>}
      </div>
    </LightboxWrapper>
  );
}

export default DMArticle;
